import { ref } from 'vue'
import apiInstance from '@/utils/api'

export interface Kiosk {
  id: number
  email: string
  status: number
  creation_date: string
  sot_auto_login: string
  // null = never paired, 'ok' = healthy, else the resolved root cause.
  printer_status: string | null
  use_v8_kiosk: boolean
}

export interface KioskResponse {
  error: boolean
  message: string
  data: { terminals: Kiosk[]; licenses: any[] }
}

export interface KioskPrinter {
  id: number
  printer_name: string | null
  serial_number: string | null
  vendor_id: string | null
  product_id: string | null
  is_active: boolean
  status: string
  last_alert_at: string | null
  updated_at: string
}

export interface KioskPrintersResponse {
  error: boolean
  message: string
  data: { printers: KioskPrinter[] }
}

export interface StartupMedia {
  fileName: string | null
  fileData: string | null
  type: 'image' | 'video' | ''
  mediaId: string | number | null
}

export interface SOTAccount {
  id: string
  accountName: string
  password: string
  receptionTerminalAccount: string
  status: 'active' | 'inactive'
  startupMedia: StartupMedia
  startupMediaLandscape: StartupMedia
  interfaceSettings: {
    terminalId: string
    waiterId: string
    waiterPassword: string
    bookToFollowingTable: string
  }
  menuSettings: {
    showWishesAndComments: boolean
    // noPopupOnTitleDescription: boolean
    diningChoice: '1' | '2' | '3'
  }
  terminalSettings: {
    showWishesAndComments: boolean
    showTableNumberInput: boolean
    showFirstName: boolean
    showLastName: boolean
    showRoomNumber: boolean
    showSelectListInOrderBasket: boolean
    selectListLabel: string
    selectListOptions: string[]
    automaticOrderCancellation: boolean
    paymentEnabled: boolean
    paymentTerminal: string
    tipOptionOnTerminal: boolean
    orderConfirmation: boolean
    orderConfirmationMessage: string
    printReceiptEnabled: boolean
  }
  kioskSettings: {
    // '' = not set (no minimum); BaseInput emits '' for an empty number field.
    minimumOrderAmount: number | ''
    priceLevel: string
  }
  restaurantId: string
  createdAt: string
  updatedAt: string
}

export interface SOTAccountDraft extends Omit<SOTAccount, 'id' | 'createdAt' | 'updatedAt'> {
  validationError?: string
  fieldErrors?: Record<string, string[]>
  showPassword?: boolean
}

const createEmptyDraft = (): SOTAccountDraft => ({
  accountName: '',
  password: '',
  receptionTerminalAccount: '',
  status: 'active',
  startupMedia: {
    fileName: null,
    fileData: null,
    type: '',
    mediaId: null,
  },
  startupMediaLandscape: {
    fileName: null,
    fileData: null,
    type: '',
    mediaId: null,
  },
  interfaceSettings: {
    terminalId: '',
    waiterId: '',
    waiterPassword: '',
    bookToFollowingTable: '',
  },
  menuSettings: {
    showWishesAndComments: false,
    // noPopupOnTitleDescription: false,
    diningChoice: '1',
  },
  terminalSettings: {
    showWishesAndComments: false,
    showTableNumberInput: false,
    showFirstName: false,
    showLastName: false,
    showRoomNumber: false,
    showSelectListInOrderBasket: false,
    selectListLabel: '',
    selectListOptions: [],
    automaticOrderCancellation: false,
    paymentEnabled: false,
    paymentTerminal: '',
    tipOptionOnTerminal: false,
    orderConfirmation: false,
    orderConfirmationMessage: '',
    printReceiptEnabled: false,
  },
  kioskSettings: {
    minimumOrderAmount: 0,
    priceLevel: '',
  },
  restaurantId: '',
  validationError: '',
  fieldErrors: {},
  showPassword: false,
})

export const useSOTAccounts = () => {
  const accounts = ref<SOTAccount[]>([
    {
      id: '1',
      accountName: 'Kiosk Main Counter',
      password: 'kiosk123456',
      receptionTerminalAccount: 'main-counter',
      status: 'active',
      startupMedia: {
        fileName: 'welcome.jpg',
        fileData: null,
        type: 'image',
        mediaId: null,
      },
      startupMediaLandscape: {
        fileName: null,
        fileData: null,
        type: '',
        mediaId: null,
      },
      interfaceSettings: {
        terminalId: 'T001',
        waiterId: 'W001',
        waiterPassword: 'waiter123',
        bookToFollowingTable: '1,2,3,4,5',
      },
      menuSettings: {
        showWishesAndComments: true,
        // noPopupOnTitleDescription: false,
        diningChoice: '3',
      },
      terminalSettings: {
        showWishesAndComments: true,
        showTableNumberInput: true,
        showFirstName: true,
        showLastName: true,
        showRoomNumber: false,
        showSelectListInOrderBasket: true,
        selectListLabel: 'Dining Style',
        selectListOptions: ['Dine In', 'Takeaway', 'Delivery'],
        automaticOrderCancellation: false,
        paymentEnabled: true,
        paymentTerminal: 'ADYEN001',
        tipOptionOnTerminal: true,
        orderConfirmation: true,
        orderConfirmationMessage: 'Please review your order before proceeding to payment.',
        printReceiptEnabled: false,
      },
      kioskSettings: {
        minimumOrderAmount: 0,
        priceLevel: '',
      },
      restaurantId: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '2',
      accountName: 'Kiosk Entrance',
      password: 'entrance2024',
      receptionTerminalAccount: 'entrance',
      status: 'active',
      startupMedia: {
        fileName: null,
        fileData: null,
        type: '',
        mediaId: null,
      },
      startupMediaLandscape: {
        fileName: null,
        fileData: null,
        type: '',
        mediaId: null,
      },
      interfaceSettings: {
        terminalId: 'T002',
        waiterId: 'W002',
        waiterPassword: 'entrance99',
        bookToFollowingTable: '1-20',
      },
      menuSettings: {
        showWishesAndComments: false,
        // noPopupOnTitleDescription: false,
        diningChoice: '2',
      },
      terminalSettings: {
        showWishesAndComments: false,
        showTableNumberInput: true,
        showFirstName: true,
        showLastName: false,
        showRoomNumber: false,
        showSelectListInOrderBasket: false,
        selectListLabel: '',
        selectListOptions: [],
        automaticOrderCancellation: true,
        paymentEnabled: false,
        paymentTerminal: '',
        tipOptionOnTerminal: false,
        orderConfirmation: false,
        orderConfirmationMessage: '',
        printReceiptEnabled: false,
      },
      kioskSettings: {
        minimumOrderAmount: 0,
        priceLevel: '',
      },
      restaurantId: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '3',
      accountName: 'Kiosk Patio',
      password: 'patio5678',
      receptionTerminalAccount: 'patio-area',
      status: 'inactive',
      startupMedia: {
        fileName: 'patio_menu.png',
        fileData: null,
        type: 'image',
        mediaId: null,
      },
      startupMediaLandscape: {
        fileName: null,
        fileData: null,
        type: '',
        mediaId: null,
      },
      interfaceSettings: {
        terminalId: 'T003',
        waiterId: 'W003',
        waiterPassword: 'patio123',
        bookToFollowingTable: '10,11,12,13',
      },
      menuSettings: {
        showWishesAndComments: true,
        // noPopupOnTitleDescription: true,
        diningChoice: '1',
      },
      terminalSettings: {
        showWishesAndComments: true,
        showTableNumberInput: true,
        showFirstName: true,
        showLastName: true,
        showRoomNumber: true,
        showSelectListInOrderBasket: true,
        selectListLabel: 'Service Type',
        selectListOptions: ['Table Service', 'Bar Service'],
        automaticOrderCancellation: false,
        paymentEnabled: true,
        paymentTerminal: 'ADYEN002',
        tipOptionOnTerminal: false,
        orderConfirmation: true,
        orderConfirmationMessage: 'Confirm your order to proceed.',
        printReceiptEnabled: false,
      },
      kioskSettings: {
        minimumOrderAmount: 0,
        priceLevel: '',
      },
      restaurantId: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ])

  const draft = ref<SOTAccountDraft>(createEmptyDraft())

  const initializeDraft = (accountId?: string) => {
    if (accountId) {
      const account = getAccountById(accountId)
      if (account) {
        draft.value = {
          accountName: account.accountName,
          password: account.password,
          receptionTerminalAccount: account.receptionTerminalAccount,
          status: account.status,
          startupMedia: { ...account.startupMedia },
          startupMediaLandscape: { ...account.startupMediaLandscape },
          interfaceSettings: { ...account.interfaceSettings },
          menuSettings: { ...account.menuSettings },
          terminalSettings: { ...account.terminalSettings },
          kioskSettings: { ...account.kioskSettings },
          restaurantId: account.restaurantId,
        }
      }
    } else {
      draft.value = createEmptyDraft()
    }
  }

  const updateDraft = (updates: Partial<SOTAccountDraft>) => {
    draft.value = { ...draft.value, ...updates }
  }

  const getDraft = () => draft.value

  const clearDraft = () => {
    draft.value = createEmptyDraft()
  }

  const createAccount = (data: Omit<SOTAccount, 'id' | 'createdAt' | 'updatedAt'>): SOTAccount => {
    const newAccount: SOTAccount = {
      ...data,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    accounts.value.push(newAccount)
    return newAccount
  }

  const updateAccount = (id: string, data: Partial<Omit<SOTAccount, 'id' | 'createdAt' | 'accountName'>>) => {
    const index = accounts.value.findIndex((a) => a.id === id)
    if (index !== -1) {
      accounts.value[index] = {
        ...accounts.value[index],
        ...data,
        updatedAt: new Date().toISOString(),
      }
    }
  }

  const deleteAccount = (id: string) => {
    const index = accounts.value.findIndex((a) => a.id === id)
    if (index !== -1) {
      accounts.value.splice(index, 1)
    }
  }

  const toggleAccountStatus = (id: string) => {
    const account = accounts.value.find((a) => a.id === id)
    if (account) {
      account.status = account.status === 'active' ? 'inactive' : 'active'
      account.updatedAt = new Date().toISOString()
    }
  }

  const getAccountById = (id: string) => {
    return accounts.value.find((a) => a.id === id)
  }

  const getRestaurantAccounts = (restaurantId: string) => {
    return accounts.value.filter((a) => a.restaurantId === restaurantId)
  }

  const initializeRestaurantAccounts = (restaurantId: string) => {
    accounts.value.forEach((account) => {
      account.restaurantId = restaurantId
    })
  }

  // Kiosks from self-terminal API
  const kiosks = ref<Kiosk[]>([])
  const kioskLoading = ref(false)
  const kioskError = ref<string | null>(null)

  const fetchKiosks = async (restaurantId: string): Promise<Kiosk[]> => {
    kioskLoading.value = true
    kioskError.value = null

    try {
      const response = await apiInstance.get<KioskResponse>(
        `/restaurant/${restaurantId}/self-terminal`
      )

      if (!response.data.error && response.data.data) {
        const terminals = Array.isArray(response.data.data.terminals) ? response.data.data.terminals : []
        kiosks.value = terminals
        return terminals
      } else {
        kioskError.value = response.data.message || 'Failed to fetch kiosks'
        kiosks.value = []
        return []
      }
    } catch (err: any) {
      kioskError.value = err?.response?.data?.message || err?.message || 'Failed to fetch kiosks'
      kiosks.value = []
      console.error('Error fetching kiosks:', err)
      return []
    } finally {
      kioskLoading.value = false
    }
  }

  const fetchKioskPrinters = async (restaurantId: string, kioskId: number): Promise<KioskPrinter[]> => {
    try {
      const response = await apiInstance.get<KioskPrintersResponse>(
        `/restaurant/${restaurantId}/self-terminal/${kioskId}/printers`
      )

      if (!response.data.error && response.data.data) {
        return Array.isArray(response.data.data.printers) ? response.data.data.printers : []
      }
      return []
    } catch (err: any) {
      console.error('Error fetching kiosk printers:', err)
      return []
    }
  }

  const deleteKiosk = async (restaurantId: string, kioskId: number): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await apiInstance.delete(
        `/restaurant/${restaurantId}/self-terminal/${kioskId}`
      )

      if (!response.data.error) {
        kiosks.value = kiosks.value.filter(k => k.id !== kioskId)
        kioskError.value = null
        return { success: true }
      } else {
        const errorMsg = response.data.message || 'Failed to delete kiosk'
        kioskError.value = errorMsg
        return { success: false, message: errorMsg }
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.message || 'Failed to delete kiosk'
      kioskError.value = errorMsg
      console.error('Error deleting kiosk:', err)
      return { success: false, message: errorMsg }
    }
  }

  const updateKioskVersion = async (
    restaurantId: string,
    kioskId: number,
    useV8Kiosk: boolean
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await apiInstance.put(
        `/restaurant/${restaurantId}/self-terminal/${kioskId}/kiosk-version`,
        { use_v8_kiosk: useV8Kiosk ? 1 : 0 }
      )

      if (!response.data.error) {
        // Re-fetch rather than patch use_v8_kiosk in place: sot_auto_login's displayed
        // URL format depends on this flag too, and only the backend builds that string.
        await fetchKiosks(restaurantId)
        kioskError.value = null
        return { success: true }
      } else {
        const errorMsg = response.data.message || 'Failed to update kiosk version'
        kioskError.value = errorMsg
        return { success: false, message: errorMsg }
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.message || 'Failed to update kiosk version'
      kioskError.value = errorMsg
      console.error('Error updating kiosk version:', err)
      return { success: false, message: errorMsg }
    }
  }

  const uploadStartupMedia = async (restaurantId: string, file: File, typeId?: string | number, orientation: 'portrait' | 'landscape' = 'portrait'): Promise<{ mediaId: string | number; mediaUrl: string } | null> => {
    if (!restaurantId || restaurantId.trim() === '') {
      console.error('🔴 Restaurant ID is not available for upload. Received:', { restaurantId, type: typeof restaurantId })
      return null
    }

    try {
      const formData = new FormData()
      formData.append('type', orientation === 'landscape' ? 'terminal_landscape' : 'terminal')
      if (typeId) {
        formData.append('type_id', String(typeId))
      }
      formData.append('media[]', file)

      const response = await apiInstance.post(`/restaurant/${restaurantId}/upload-media`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      const data = response.data

      if (data.error === false && data.data) {
        let mediaId: string | number | null = null
        let mediaUrl: string | null = null

        // Handle the new response format where media_ids contains objects
        if (data.data.media_ids && Array.isArray(data.data.media_ids) && data.data.media_ids.length > 0) {
          const mediaItem = data.data.media_ids[0] // Assuming only one media item is uploaded at a time
          if (typeof mediaItem === 'object' && mediaItem.id) {
            // New format: {id, mime_type, original_url}
            mediaId = mediaItem.id
            mediaUrl = mediaItem.original_url
          } else {
            // Fallback for older format where it's just the ID
            mediaId = mediaItem
          }
        } else if (data.data.id) {
          mediaId = data.data.id
        } else if (data.data.media_id) {
          mediaId = data.data.media_id
        }

        if (mediaId) {
          const result = {
            mediaId: mediaId,
            mediaUrl: mediaUrl || file.name, // Use original_url if available, else fallback to file name
          }
          return result
        }
      }

      if (data.error === true && data.errors) {
        console.warn('⚠️ Backend validation error:', data.errors)
        const error = new Error(data.message || 'Validation error')
        ;(error as any).validationErrors = data.errors
        throw error
      }

      console.warn('⚠️ Upload response indicates failure or missing media_ids:', data)
      return null
    } catch (error) {
      console.error('🔴 Error uploading media:', error)
      throw error
    }
  }

  const deleteStartupMedia = async (restaurantId: string, mediaId: string | number, typeId?: string | number, orientation: 'portrait' | 'landscape' = 'portrait'): Promise<boolean> => {
    if (!restaurantId || restaurantId.trim() === '') {
      console.error('🔴 Restaurant ID is not available for media deletion. Received:', { restaurantId, type: typeof restaurantId })
      return false
    }

    if (!mediaId) {
      console.error('🔴 Media ID is not available for deletion. Received:', { mediaId, type: typeof mediaId })
      return false
    }

    try {
      const payload: Record<string, any> = {
        type: orientation === 'landscape' ? 'terminal_landscape' : 'terminal',
      }
      if (typeId) {
        payload.type_id = String(typeId)
      }

      const response = await apiInstance.delete(`/restaurant/${restaurantId}/delete-media/${mediaId}`, {
        data: payload,
      })

      const data = response.data

      if (data.error === false || response.status === 200 || response.status === 204) {
        return true
      }

      console.warn('⚠️ Delete response indicates failure:', data)
      return false
    } catch (error) {
      console.error('🔴 Error deleting media:', error)
      return false
    }
  }

  // Get the next suggested account name (<restaurant_domain>-sot-<n>) from the API
  const suggestAccountName = async (restaurantId: string): Promise<string | null> => {
    try {
      const response = await apiInstance.get(`/restaurant/${restaurantId}/self-terminal/suggest-account-name`)
      const data = response.data

      if (data.error === false && data.data?.account_name) {
        return data.data.account_name as string
      }

      return null
    } catch (e: any) {
      console.warn('⚠️ Could not suggest self-terminal account name:', e.message)
      return null
    }
  }

  const fetchSOTAccountDetails = async (restaurantId: string, accountId: string | number): Promise<SOTAccount | null> => {
    try {

      const response = await apiInstance.get(`/restaurant/${restaurantId}/self-terminal/${accountId}`)

      const data = response.data

      if (data.error === false && data.data) {
        const apiData = data.data
        // Map API response to SOTAccount format
        const account: SOTAccount = {
          id: String(apiData.id || accountId),
          accountName: apiData.email || '',
          password: apiData.password || '',
          receptionTerminalAccount: String(apiData.reception_terminal_id || ''),
          status: apiData.status === 1 ? 'active' : 'inactive',
          startupMedia: {
            fileName: apiData.media?.length > 0 ? (apiData.media[0].original_filename || 'Media') : null,
            fileData: apiData.media?.length > 0 ? apiData.media[0].original_url : null,
            type: apiData.media?.length > 0 ? (apiData.media[0].mime_type?.startsWith('image/') ? 'image' : 'video') : '',
            mediaId: apiData.media?.length > 0 ? apiData.media[0].id : null,
          },
          startupMediaLandscape: {
            fileName: apiData.media_landscape?.length > 0 ? (apiData.media_landscape[0].original_filename || 'Media') : null,
            fileData: apiData.media_landscape?.length > 0 ? apiData.media_landscape[0].original_url : null,
            type: apiData.media_landscape?.length > 0 ? (apiData.media_landscape[0].mime_type?.startsWith('image/') ? 'image' : 'video') : '',
            mediaId: apiData.media_landscape?.length > 0 ? apiData.media_landscape[0].id : null,
          },
          interfaceSettings: {
            terminalId: apiData.terminal_id || '',
            waiterId: apiData.waiter_id || '',
            waiterPassword: apiData.waiter_password || '',
            bookToFollowingTable: apiData.interface_table_no || '',
          },
          menuSettings: {
            showWishesAndComments: apiData.show_wishes_and_comments === 1,
            // noPopupOnTitleDescription: apiData.hide_title_description_popup === 1,
            diningChoice: String(apiData.enable_eat_here_take_away || '1') as '1' | '2' | '3',
          },
          terminalSettings: {
            showWishesAndComments: apiData.show_wishes_comments === 1,
            showTableNumberInput: apiData.show_table_number === 1,
            showFirstName: apiData.show_first_name === 1,
            showLastName: apiData.show_last_name === 1,
            showRoomNumber: apiData.show_room_no === 1,
            showSelectListInOrderBasket: apiData.show_select_list === 1,
            selectListLabel: apiData.select_list_label || '',
            selectListOptions: apiData.select_list_options ? apiData.select_list_options.split(',').map((opt: string) => opt.trim()).filter((opt: string) => opt.length > 0) : [],
            automaticOrderCancellation: apiData.automatic_order_cancel === 1,
            paymentEnabled: apiData.payment_status === 1,
            paymentTerminal: apiData.adyen_terminal_name || '',
            tipOptionOnTerminal: apiData.terminal_tip_option === 1,
            orderConfirmation: apiData.confirm_message_active === 1,
            orderConfirmationMessage: apiData.confirmation_msg || '',
            printReceiptEnabled: apiData.print_receipt_enabled === true,
          },
          kioskSettings: {
            minimumOrderAmount: apiData.kiosk_min_order_amount != null ? Number(apiData.kiosk_min_order_amount) : '',
            priceLevel: apiData.kiosk_price_level_id ? String(apiData.kiosk_price_level_id) : '',
          },
          restaurantId: restaurantId,
          createdAt: apiData.created_at || new Date().toISOString(),
          updatedAt: apiData.updated_at || new Date().toISOString(),
        }
        return account
      } else {
        console.warn('⚠️ Fetch response indicates error:', data.message)
        return null
      }
    } catch (error: any) {
      console.error('🔴 Error fetching SOT account details:', error)
      throw error
    }
  }

  const updateSOTAccountAPI = async (
    restaurantId: string,
    accountId: string | number,
    data: Omit<SOTAccount, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<boolean> => {
    try {
      const payload: Record<string, any> = {
        reception_terminal_id: data.receptionTerminalAccount ? parseInt(data.receptionTerminalAccount) : null,
        status: data.status === 'active' ? 1 : 0,
        // hide_title_description_popup: data.menuSettings.noPopupOnTitleDescription ? 1 : 0,
        show_wishes_and_comments: data.menuSettings.showWishesAndComments ? 1 : 0,
        enable_eat_here_take_away: parseInt(data.menuSettings.diningChoice),
        show_table_number: data.terminalSettings.showTableNumberInput ? 1 : 0,
        show_first_name: data.terminalSettings.showFirstName ? 1 : 0,
        show_last_name: data.terminalSettings.showLastName ? 1 : 0,
        show_room_no: data.terminalSettings.showRoomNumber ? 1 : 0,
        show_select_list: data.terminalSettings.showSelectListInOrderBasket ? 1 : 0,
        select_list_label: data.terminalSettings.selectListLabel || '',
        select_list_options: data.terminalSettings.selectListOptions.join(', '),
        show_wishes_comments: data.terminalSettings.showWishesAndComments ? 1 : 0,
        automatic_order_cancel: data.terminalSettings.automaticOrderCancellation ? 1 : 0,
        payment_status: data.terminalSettings.paymentEnabled ? 1 : 0,
        adyen_terminal_name: data.terminalSettings.paymentTerminal || '',
        terminal_tip_option: data.terminalSettings.tipOptionOnTerminal ? 1 : 0,
        confirm_message_active: data.terminalSettings.orderConfirmation ? 1 : 0,
        confirmation_msg: data.terminalSettings.orderConfirmationMessage || '',
        print_receipt_enabled: data.terminalSettings.printReceiptEnabled ? 1 : 0,
        kiosk_min_order_amount: data.kioskSettings.minimumOrderAmount === '' ? null : data.kioskSettings.minimumOrderAmount,
        kiosk_price_level_id: parseInt(data.kioskSettings.priceLevel) || null,
        media_ids: data.startupMedia.mediaId ? [parseInt(String(data.startupMedia.mediaId))] : [],
        media_ids_landscape: data.startupMediaLandscape.mediaId ? [parseInt(String(data.startupMediaLandscape.mediaId))] : [],
        terminal_id: data.interfaceSettings.terminalId,
        waiter_id: data.interfaceSettings.waiterId,
        waiter_password: data.interfaceSettings.waiterPassword,
        interface_table_no: data.interfaceSettings.bookToFollowingTable || '',
      }

      // Only include password if it's provided (not empty)
      if (data.password && data.password.trim()) {
        payload.password = data.password
      }

      const response = await apiInstance.put(`/restaurant/${restaurantId}/self-terminal/${accountId}`, payload)

      if (response.data.error === false) {
        return true
      } else {
        console.warn('⚠️ Update response indicates error:', response.data.message)
        const error = new Error(response.data.message || 'Failed to update SOT Account')
        ;(error as any).validationErrors = response.data.errors
        throw error
      }
    } catch (error) {
      console.error('🔴 Error updating SOT Account:', error)
      throw error
    }
  }

  return {
    accounts,
    draft,
    createAccount,
    updateAccount,
    deleteAccount,
    toggleAccountStatus,
    getAccountById,
    getRestaurantAccounts,
    initializeRestaurantAccounts,
    initializeDraft,
    updateDraft,
    getDraft,
    clearDraft,
    // Kiosks
    kiosks,
    kioskLoading,
    kioskError,
    fetchKiosks,
    deleteKiosk,
    updateKioskVersion,
    fetchKioskPrinters,
    // Media
    uploadStartupMedia,
    deleteStartupMedia,
    // API operations
    suggestAccountName,
    fetchSOTAccountDetails,
    updateSOTAccountAPI,
  }
}
